import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <main className="privacy">
      <div className="privacy__container">

        <header className="privacy__header">
          <p className="privacy__eyebrow">Legal</p>
          <h1 className="privacy__title">Privacy Policy</h1>
          <p className="privacy__effective">Effective Date: May 27, 2026</p>
        </header>

        <div className="privacy__divider" />

        <div className="privacy__body">

          <p className="privacy__intro">
            At RentWise, your privacy is not a product feature — it is a foundational
            commitment. This Privacy Policy explains what information we collect, how we
            use it, who we share it with, and how we protect it. We encourage you to read
            this document in full. If you have any questions, contact us at{' '}
            <a href="mailto:support@rentwiseapp.co" className="privacy__link">
              support@rentwiseapp.co
            </a>.
          </p>

          <section className="privacy__section">
            <h2 className="privacy__section-title">1. Our Core Commitment: We Will Never Sell Your Data</h2>
            <p className="privacy__text">
              RentWise will never sell, rent, trade, or auction your personal information
              to any third party — under any circumstances, for any price. This is not a
              marketing promise. It is a structural commitment baked into how we operate.
              Our business model is built around helping renters, not monetizing them.
            </p>
            <p className="privacy__text">
              We do not sell data to advertisers, data brokers, analytics firms, or any
              other third parties who would use your information for their own commercial
              purposes. We do not operate advertising networks, and we do not build
              advertising profiles on our users.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">2. Who We Are</h2>
            <p className="privacy__text">
              RentWise ("RentWise," "we," "us," or "our") is a technology company building
              tools that empower renters to protect themselves, document their rental
              experiences, and engage in fair dealings with landlords. We operate the
              RentWise web application and any associated mobile applications (collectively,
              the "Service").
            </p>
            <p className="privacy__text">
              If you have questions about this policy or our data practices, contact us
              at:{' '}
              <a href="mailto:support@rentwiseapp.co" className="privacy__link">
                support@rentwiseapp.co
              </a>.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">3. Information We Collect</h2>
            <p className="privacy__text">
              We collect only the information necessary to provide and improve our Service.
              Below is a precise breakdown of each category.
            </p>

            <h3 className="privacy__subsection-title">3a. Information You Provide Directly</h3>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Email address.</strong> When you join our waitlist or create an
                account, we collect your email address. We use this to send you updates
                about RentWise, your place in the waitlist, product announcements, and
                important service notifications. You may unsubscribe from marketing emails
                at any time.
              </li>
              <li className="privacy__list-item">
                <strong>Name.</strong> If you provide your name during account creation or
                profile setup, we store it to personalize your experience and identify
                your account.
              </li>
              <li className="privacy__list-item">
                <strong>Payment information.</strong> When you make a payment or set up
                billing, your payment card details are collected and processed entirely by
                Stripe, Inc. RentWise never receives, sees, or stores your raw credit card
                numbers, CVV codes, or full payment card data. We receive only tokenized
                identifiers and metadata (such as card brand, last four digits, and
                expiration date) from Stripe.
              </li>
              <li className="privacy__list-item">
                <strong>Financial account information (landlords).</strong> Landlords who
                use RentWise to receive payouts must connect a bank account or debit card
                through Stripe Financial Connections (part of Stripe Financial Connect).
                This process is handled entirely by Stripe. RentWise does not store your
                raw bank account numbers or routing numbers. Stripe may, with your consent,
                access account balance and transaction history data to verify account
                ownership and facilitate payouts.
              </li>
            </ul>

            <h3 className="privacy__subsection-title">3b. Information Collected Automatically</h3>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Authentication data.</strong> We use Google Firebase Authentication
                to manage user sign-in. When you create an account or sign in, Firebase
                handles credential verification and issues authentication tokens. We store
                a Firebase User ID (UID) as your unique account identifier in our database.
              </li>
              <li className="privacy__list-item">
                <strong>Device and usage data.</strong> Firebase and our hosting
                infrastructure may automatically collect technical information such as your
                IP address, browser type, operating system, device identifiers, and pages
                visited within the Service. This data is used for security, performance
                monitoring, and debugging purposes only.
              </li>
              <li className="privacy__list-item">
                <strong>Rental documentation and records.</strong> If you use RentWise to
                document your rental property, upload photos, record communications, or log
                maintenance requests, this content is stored in Google Firebase Firestore
                and Google Firebase Storage. This data belongs to you and is not used for
                any purpose other than operating your account.
              </li>
            </ul>

            <h3 className="privacy__subsection-title">3c. Information We Do Not Collect</h3>
            <p className="privacy__text">
              We do not collect your Social Security Number, government-issued ID numbers,
              or full bank account numbers directly. We do not collect sensitive demographic
              data such as race, religion, or political affiliation. We do not run
              behavioral advertising or build interest-based advertising profiles.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">4. How We Use Your Information</h2>
            <p className="privacy__text">
              We use the information we collect solely to operate and improve the Service:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                To create and manage your account and authenticate your identity.
              </li>
              <li className="privacy__list-item">
                To process payments and disburse landlord payouts via Stripe.
              </li>
              <li className="privacy__list-item">
                To store and retrieve your rental records, documentation, and uploaded
                files.
              </li>
              <li className="privacy__list-item">
                To send you transactional communications (account confirmations, security
                alerts, payment receipts) and, where you have opted in, product updates
                and waitlist notifications.
              </li>
              <li className="privacy__list-item">
                To detect, investigate, and prevent fraudulent transactions, abuse, and
                violations of our Terms of Service.
              </li>
              <li className="privacy__list-item">
                To debug technical issues, maintain the reliability of our infrastructure,
                and improve the performance of the Service.
              </li>
              <li className="privacy__list-item">
                To comply with applicable laws and respond to valid legal process.
              </li>
            </ul>
            <p className="privacy__text">
              We do not use your information to build advertising profiles, sell targeted
              ad inventory, or infer sensitive attributes about you.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">5. Third-Party Services We Use</h2>
            <p className="privacy__text">
              We work with a small, carefully selected set of third-party service providers
              who process data on our behalf. Each is bound by its own privacy policies and,
              where applicable, data processing agreements with us.
            </p>

            <h3 className="privacy__subsection-title">5a. Google Firebase</h3>
            <p className="privacy__text">
              We use several Firebase products operated by Google LLC:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Firebase Authentication</strong> — manages user sign-up, login,
                password resets, and session tokens. Firebase stores hashed credentials
                and issues JWTs to the client.
              </li>
              <li className="privacy__list-item">
                <strong>Firebase Firestore</strong> — our primary NoSQL database. Your
                account data, rental records, and application state are stored here.
                Data is stored in Google's cloud infrastructure and subject to Google's
                security certifications (SOC 2, ISO 27001).
              </li>
              <li className="privacy__list-item">
                <strong>Firebase Storage</strong> — stores user-uploaded files such as
                move-in/move-out photos, lease documents, and repair records. Files are
                protected by Firebase Security Rules tied to your authenticated identity.
              </li>
            </ul>
            <p className="privacy__text">
              Google's privacy policy is available at{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="privacy__link"
              >
                policies.google.com/privacy
              </a>.
            </p>

            <h3 className="privacy__subsection-title">5b. Stripe</h3>
            <p className="privacy__text">
              We use Stripe, Inc. for all payment processing. Stripe is a PCI DSS Level 1
              certified payment processor. We use two distinct Stripe products:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Stripe Payments</strong> — handles charge collection for any paid
                features or services. Stripe collects and processes payment card data under
                its own PCI-compliant infrastructure. RentWise receives only tokenized
                references and never touches raw card data.
              </li>
              <li className="privacy__list-item">
                <strong>Stripe Financial Connect</strong> — used to connect landlord bank
                accounts for payout disbursement. When a landlord connects their bank
                account, they interact directly with Stripe's secure, consent-based flow.
                Stripe may access read-only financial account data (balances, account
                details) with the landlord's explicit consent, strictly for verifying the
                account and facilitating payouts. This data is governed by Stripe's
                Financial Connections data usage agreement, which landlords must
                independently accept.
              </li>
            </ul>
            <p className="privacy__text">
              Stripe's privacy policy is available at{' '}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="privacy__link"
              >
                stripe.com/privacy
              </a>.
            </p>

            <h3 className="privacy__subsection-title">5c. No Other Material Third Parties</h3>
            <p className="privacy__text">
              Beyond Firebase and Stripe, RentWise does not share your personal data with
              advertisers, data brokers, social networks, or analytics companies that would
              use it for their own commercial purposes. If this changes, we will update
              this policy and notify you in advance.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">6. Data Retention</h2>
            <p className="privacy__text">
              We retain your personal data for as long as your account is active or as
              needed to provide you the Service:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Active accounts:</strong> We retain all account data and uploaded
                content for the lifetime of your active account.
              </li>
              <li className="privacy__list-item">
                <strong>Deleted accounts:</strong> When you delete your account, we will
                permanently delete your personal data and uploaded files within 30 days,
                except where we are required to retain records for legal or regulatory
                compliance (for example, payment records for tax purposes, which may be
                retained for up to 7 years).
              </li>
              <li className="privacy__list-item">
                <strong>Waitlist data:</strong> Email addresses collected through the
                waitlist are retained until the waitlist program concludes or until you
                unsubscribe, whichever comes first.
              </li>
              <li className="privacy__list-item">
                <strong>Firebase Authentication logs:</strong> Firebase may retain
                authentication event logs for security and fraud detection purposes for
                a period governed by Google's data retention policies.
              </li>
            </ul>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">7. Security</h2>
            <p className="privacy__text">
              We take reasonable and industry-standard technical and organizational measures
              to protect your personal data against unauthorized access, alteration,
              disclosure, or destruction:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                All data transmitted between your browser and our servers is encrypted
                using TLS (Transport Layer Security).
              </li>
              <li className="privacy__list-item">
                Firebase Security Rules enforce that each authenticated user can only
                access their own data in Firestore and Storage.
              </li>
              <li className="privacy__list-item">
                Payment data is handled entirely by Stripe's PCI DSS Level 1 certified
                infrastructure — we do not process or store raw payment credentials.
              </li>
              <li className="privacy__list-item">
                We do not store passwords — Firebase Authentication manages credential
                storage using industry-standard hashing.
              </li>
            </ul>
            <p className="privacy__text">
              No system is perfectly secure. If you believe your account has been
              compromised, please contact us immediately at{' '}
              <a href="mailto:support@rentwiseapp.co" className="privacy__link">
                support@rentwiseapp.co
              </a>.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">8. Your Rights and Choices</h2>
            <p className="privacy__text">
              Depending on where you live, you may have certain rights regarding your
              personal data. Regardless of your jurisdiction, we honor the following
              requests:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Access:</strong> You may request a copy of the personal data we
                hold about you.
              </li>
              <li className="privacy__list-item">
                <strong>Correction:</strong> You may update or correct inaccurate
                information in your account settings, or by contacting us.
              </li>
              <li className="privacy__list-item">
                <strong>Deletion:</strong> You may request deletion of your account and
                associated personal data. We will process deletion requests within 30
                days.
              </li>
              <li className="privacy__list-item">
                <strong>Unsubscribe:</strong> You may opt out of marketing emails at any
                time using the unsubscribe link in any email we send, or by contacting us
                directly.
              </li>
              <li className="privacy__list-item">
                <strong>Data portability:</strong> Upon request, we will provide your data
                in a machine-readable format where technically feasible.
              </li>
            </ul>
            <p className="privacy__text">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@rentwiseapp.co" className="privacy__link">
                support@rentwiseapp.co
              </a>. We will respond within 30 days. We will never discriminate against you
              for exercising your privacy rights.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">9. Children's Privacy</h2>
            <p className="privacy__text">
              The Service is not directed to children under the age of 13, and we do not
              knowingly collect personal information from children under 13. If you are a
              parent or guardian and believe we have inadvertently collected information
              from a child under 13, please contact us immediately at{' '}
              <a href="mailto:support@rentwiseapp.co" className="privacy__link">
                support@rentwiseapp.co
              </a>{' '}
              and we will delete that information promptly.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">10. Cookies and Tracking Technologies</h2>
            <p className="privacy__text">
              Our Service uses cookies and similar technologies for the following limited
              purposes:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                <strong>Authentication sessions:</strong> Firebase Authentication uses
                browser-based tokens (stored in localStorage or IndexedDB) to maintain
                your login session across page visits. These are strictly necessary for
                the Service to function.
              </li>
              <li className="privacy__list-item">
                <strong>Performance and infrastructure:</strong> Our hosting provider may
                set technical cookies required for load balancing and security.
              </li>
            </ul>
            <p className="privacy__text">
              We do not use advertising cookies, tracking pixels, or third-party analytics
              cookies that would allow external parties to track you across other websites.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">11. International Data Transfers</h2>
            <p className="privacy__text">
              RentWise is based in the United States, and our third-party service providers
              (Google Firebase and Stripe) store and process data primarily in the United
              States. If you access our Service from outside the United States, please be
              aware that your information may be transferred to, stored, and processed in
              the U.S., where data protection laws may differ from those in your country.
              By using our Service, you consent to this transfer.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">12. Changes to This Privacy Policy</h2>
            <p className="privacy__text">
              We may update this Privacy Policy from time to time to reflect changes in our
              practices, our Service, or applicable law. When we make material changes, we
              will notify you by:
            </p>
            <ul className="privacy__list">
              <li className="privacy__list-item">
                Updating the "Effective Date" at the top of this page.
              </li>
              <li className="privacy__list-item">
                Sending an email notification to your registered email address for changes
                that significantly affect your rights.
              </li>
              <li className="privacy__list-item">
                Displaying a prominent notice within the Service.
              </li>
            </ul>
            <p className="privacy__text">
              Your continued use of the Service after the effective date of any revised
              policy constitutes your acceptance of the changes. If you do not agree with
              the updated policy, you may delete your account and discontinue use of the
              Service.
            </p>
          </section>

          <section className="privacy__section">
            <h2 className="privacy__section-title">13. Contact Us</h2>
            <p className="privacy__text">
              If you have any questions, concerns, or requests regarding this Privacy Policy
              or our data practices, please contact us:
            </p>
            <div className="privacy__contact-block">
              <p className="privacy__text"><strong>RentWise</strong></p>
              <p className="privacy__text">
                Email:{' '}
                <a href="mailto:support@rentwiseapp.co" className="privacy__link">
                  support@rentwiseapp.co
                </a>
              </p>
            </div>
            <p className="privacy__text">
              We are committed to resolving any complaints about your privacy and our
              collection or use of your personal data. We will respond to all inquiries
              within 30 days.
            </p>
          </section>

        </div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
